import { motion } from "framer-motion";
import { BookOpen, Pill, Clock, HeartHandshake } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const Card = ({ testid, className, children, delay }) => (
  <motion.article data-testid={testid}
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ y: -6 }}
    className={`relative rounded-[28px] border border-white/10 overflow-hidden p-6 sm:p-8 ${className}`}>
    {children}
  </motion.article>
);

export const Education = () => (
  <section id="learn" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-36">
    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
      <div>
        <Reveal><Eyebrow>Sex education</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1]">
            Know more.<br /><span className="outline-text">Worry less.</span>
          </h2>
        </Reveal>
      </div>
      <Reveal delay={0.1}><p className="text-slate-400 max-w-sm">Straight-talking basics, written without shame — for every body and every line of work.</p></Reveal>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 md:gap-5 md:auto-rows-[260px]">
      <Card testid="bento-education-card-1" delay={0} className="md:col-span-2 md:row-span-2 min-h-[360px] flex flex-col justify-end">
        <img src="/img/microscope.jpg" alt="Lab microscope" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070A14] via-[#070A14]/60 to-transparent" />
        <div className="relative">
          <Clock className="text-[#5B95D6]" />
          <h3 className="font-display text-2xl sm:text-4xl font-bold mt-4 max-w-lg">Why 14 days is the standard</h3>
          <p className="text-slate-300 mt-3 max-w-lg">Many sets and partners ask for results no older than two weeks. Testing on a steady rhythm means you're always ready — and you catch things early.</p>
        </div>
      </Card>
      <Card testid="bento-education-card-2" delay={0.08} className="bg-[#FF2A8D] text-white">
        <BookOpen />
        <h3 className="font-display text-2xl font-bold mt-4">Myths vs facts</h3>
        <p className="text-white/90 mt-2 text-sm">No symptoms doesn't mean no infection. Many STIs are silent — testing is the only way to know.</p>
      </Card>
      <Card testid="bento-education-card-3" delay={0.16} className="bg-[#141A33]">
        <Pill className="text-[#5B95D6]" />
        <h3 className="font-display text-2xl font-bold mt-4">PrEP &amp; PEP</h3>
        <p className="text-slate-300 mt-2 text-sm">PrEP helps prevent HIV ahead of time. PEP must start within 72 hours of a possible exposure — ask a provider fast.</p>
      </Card>
    </div>
    <Card testid="bento-education-card-4" delay={0.1} className="mt-4 md:mt-5 bg-gradient-to-r from-[#141A33] to-[#0E1326] flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
      <HeartHandshake className="text-[#FF2A8D] shrink-0" size={36} />
      <div>
        <h3 className="font-display text-2xl font-bold">Talking to partners</h3>
        <p className="text-slate-300 mt-1 text-sm max-w-3xl">Sharing your status is care, not confession. Your clearance pass makes the conversation quick, clear and confident.</p>
      </div>
    </Card>
  </section>
);
